import React, { useState, useRef } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { Autocomplete } from "@react-google-maps/api";
import DatePicker from "react-datepicker";
import axios from "axios";
import {
  COMPANY_NAME,
  COMPANY_PHONE_DISPLAY,
  COMPANY_PHONE_HREF,
  GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS,
  googlePlaceIsUnitedStates,
} from "../constants/company";
import "./LetsConnectModal.scss";
import { cleanupModalScrollLock } from "../utils/modalScrollCleanup";
import {
  LcIconPhone,
  LcIconInPerson,
  LcIconEnvelope,
  LcIconCheckCircle,
  LcIconClose,
  LcIconArrowLeft,
} from "./LetsConnectLineIcons";

// Import styles
import "react-datepicker/dist/react-datepicker.css";

const BUTTON_TEXT = {
  "Email": "Send Email",
  "Phone Call": "Let's Connect",
  "In-Person": "Confirm Appointment"
};

const PROJECT_TYPES = [
  "Bathroom Remodel",
  "Kitchen Remodel",
  "Home Renovation",
  "Addition",
  "ADU",
  "Exterior",
  "Other"
];

const API_ENDPOINT = `${process.env.REACT_APP_API_URL}/v1/api/appointmenForm`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** US phone: optional +1, separators, 10 digits */
const PHONE_REGEX =
  /^[+]?[1]?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

function extractFirstName(fullName) {
  const t = (fullName || "").trim();
  if (!t) return "there";
  return t.split(/\s+/)[0];
}

function clearErrorKey(setErrors, key) {
  setErrors((prev) => {
    if (!prev[key]) return prev;
    const next = { ...prev };
    delete next[key];
    return next;
  });
}

/** Machine tags for internal routing / transactional email templates (backend should honor these). */
const CONTACT_WORKFLOW_BY_OPTION = {
  "Phone Call": {
    tag: "callback_request",
    buildAutoReplySubject: (_date, timeDisplay) =>
      `We'll call you at ${timeDisplay} — ${COMPANY_NAME}`,
    autoReplyBodyHint:
      "Thanks for scheduling a call. We'll reach you at the time you selected.",
  },
  "In-Person": {
    tag: "site_visit_request",
    buildAutoReplySubject: (dateDisplay) =>
      `We'll meet you at your home on ${dateDisplay} — ${COMPANY_NAME}`,
    autoReplyBodyHint:
      "Thanks for scheduling a site visit. We'll see you at your home on the date you chose.",
  },
  Email: {
    tag: "email_inquiry",
    buildAutoReplySubject: () => `We received your message — ${COMPANY_NAME}`,
    autoReplyBodyHint: `Thank you for contacting ${COMPANY_NAME}. We've received your message and will respond soon.`,
  },
};

function formatTimeDisplay(d) {
  if (!d) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function formatDateDisplay(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Phone Call + In-Person need scheduling; Email does not. */
function contactMethodRequiresDateTime(method) {
  return method === "Phone Call" || method === "In-Person";
}

const CONTACT_FORM_COPY = {
  "Phone Call": {
    eyebrow: "PHONE CALL",
    headline: "Schedule Your Free Consultation",
    subtext:
      "Pick a time that works — we'll call you to discuss your project.",
  },
  "In-Person": {
    eyebrow: "IN-PERSON VISIT",
    headline: "Let's Meet at Your Home",
    subtext:
      "We'll come to you. Choose a time and tell us about the project.",
  },
  Email: {
    eyebrow: "EMAIL INQUIRY",
    headline: "Tell Us About Your Project",
    subtext: "Send us the details — we'll respond within 24 hours.",
  },
};

const LetsConnect = ({ letsconnect, onClose }) => {
  // State management
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [thankYouSummary, setThankYouSummary] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const autocompleteRef = useRef(null);
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    projectType: "",
    projectDescription: ""
  });
  
  const [errors, setErrors] = useState({});
  /** True only after user picks a row from Google Places (formatted_address). */
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  // Handlers
  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setShowThankYou(false);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (
      name === "phone" &&
      !/^[+]?[0-9\s.\-()]*$/.test(value)
    ) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    clearErrorKey(setErrors, name);
  };

  const handlePlaceSelect = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (place?.formatted_address) {
      if (!googlePlaceIsUnitedStates(place)) {
        setAddressConfirmed(false);
        setErrors((prev) => ({
          ...prev,
          address: "Please choose a U.S. address.",
        }));
        return;
      }
      setFormData((prev) => ({ ...prev, address: place.formatted_address }));
      setAddressConfirmed(true);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.address;
        return next;
      });
    } else {
      setAddressConfirmed(false);
    }
  };

  const handleAddressInputChange = (e) => {
    const { value } = e.target;
    setAddressConfirmed(false);
    setFormData((prev) => ({ ...prev, address: value }));
    clearErrorKey(setErrors, "address");
  };

  const validateForm = () => {
    const newErrors = {};
    const showDateTimePicker = contactMethodRequiresDateTime(selectedOption);
    const phoneRequired =
      selectedOption === "Email" || selectedOption === "Phone Call";

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (phoneRequired) {
      const p = formData.phone.trim();
      if (!p) {
        newErrors.phone = "Phone Number is required";
      } else if (!PHONE_REGEX.test(p)) {
        newErrors.phone = "Please enter a valid U.S. phone number.";
      }
    }
    const addr = formData.address.trim();
    if (addr && !addressConfirmed) {
      newErrors.address = "Please select an address from the suggestions.";
    }
    if (!formData.projectType) {
      newErrors.projectType = "Project Type is required";
    }
    if (!formData.projectDescription.trim()) {
      newErrors.projectDescription = "Project Description is required";
    }

    if (showDateTimePicker) {
      if (!selectedDate) newErrors.selectedDate = "Date is required";
      if (!selectedTime) newErrors.selectedTime = "Time is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirmAppointment = async () => {
    if (!validateForm()) return;

    const workflow = CONTACT_WORKFLOW_BY_OPTION[selectedOption];
    if (!workflow) {
      console.error("Unknown contact option:", selectedOption);
      return;
    }

    const formDataToSend = new FormData();

    let formattedDate = "";
    let formattedTime = "";

    if (selectedDate) {
      formattedDate = selectedDate.toISOString().split("T")[0];
    }

    if (selectedTime) {
      formattedTime = selectedTime.toTimeString().split(" ")[0];
    }

    const timeDisplay = formatTimeDisplay(selectedTime);
    const dateDisplay = formatDateDisplay(selectedDate);

    let suggestedAutoReplySubject = "";
    if (selectedOption === "Phone Call") {
      suggestedAutoReplySubject = workflow.buildAutoReplySubject(dateDisplay, timeDisplay);
    } else if (selectedOption === "In-Person") {
      suggestedAutoReplySubject = workflow.buildAutoReplySubject(dateDisplay);
    } else {
      suggestedAutoReplySubject = workflow.buildAutoReplySubject();
    }

    // Legacy + explicit contact fields (backend can migrate to new keys)
    formDataToSend.append("appointmenttype", selectedOption);
    formDataToSend.append("selected_contact_method", selectedOption);
    formDataToSend.append("contact_workflow_tag", workflow.tag);
    formDataToSend.append("suggested_auto_reply_subject", suggestedAutoReplySubject);
    formDataToSend.append("suggested_auto_reply_body", workflow.autoReplyBodyHint);

    formDataToSend.append("type", formData.projectType);
    formDataToSend.append("fullName", formData.fullName);
    formDataToSend.append("email", formData.email || "");
    formDataToSend.append("fulladdress", formData.address);
    formDataToSend.append("contactno", formData.phone || "");
    formDataToSend.append("projectDescription", formData.projectDescription);

    if (contactMethodRequiresDateTime(selectedOption)) {
      formDataToSend.append("date", formattedDate);
      formDataToSend.append("time", formattedTime);
      formDataToSend.append("scheduled_time_display", timeDisplay);
      formDataToSend.append("scheduled_date_display", dateDisplay);
    }

    try {
      setIsLoading(true);
      await axios.post(API_ENDPOINT, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const firstName = extractFirstName(formData.fullName);
      const isPhoneCall = selectedOption === "Phone Call";

      setThankYouSummary({
        workflowTag: workflow.tag,
        optionLabel: selectedOption,
        firstName,
        isPhoneCall,
      });
      setShowThankYou(true);
      resetForm();
    } catch (error) {
      console.error("Error submitting appointment form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      address: "",
      projectType: "",
      projectDescription: ""
    });
    setSelectedDate(null);
    setSelectedTime(null);
    setAddressConfirmed(false);
  };

  const closeModalView = () => {
    onClose?.();
    resetForm();

    // Use timeout to prevent visual glitches during modal transition
    setTimeout(() => {
      setShowThankYou(false);
      setThankYouSummary(null);
      setSelectedOption(null);
    }, 300);
  };

  const handleModalExited = () => {
    cleanupModalScrollLock();
  };

  // UI Helpers
  const renderOptionSelection = () => (
    <div className="rs-lc-step">
      <p className="rs-lc-kicker">Get in touch</p>
      <h2 className="rs-lc-title">How would you like to connect?</h2>
      <p className="rs-lc-lead">Choose one way to reach our team — we&apos;ll follow up right away.</p>
      <div className="rs-lc-options-grid" role="group" aria-label="Contact methods">
        <button
          type="button"
          className="rs-lc-option"
          onClick={() => handleOptionClick("Phone Call")}
        >
          <span className="rs-lc-option-icon">
            <LcIconPhone size={32} />
          </span>
          <span className="rs-lc-option-text">
            <span className="rs-lc-option-label">Phone Call</span>
            <span className="rs-lc-option-hint">Schedule a call</span>
          </span>
        </button>
        <button
          type="button"
          className="rs-lc-option"
          onClick={() => handleOptionClick("In-Person")}
        >
          <span className="rs-lc-option-icon">
            <LcIconInPerson size={32} />
          </span>
          <span className="rs-lc-option-text">
            <span className="rs-lc-option-label">In-Person</span>
            <span className="rs-lc-option-hint">Meet at your home</span>
          </span>
        </button>
        <button
          type="button"
          className="rs-lc-option"
          onClick={() => handleOptionClick("Email")}
        >
          <span className="rs-lc-option-icon">
            <LcIconEnvelope size={32} />
          </span>
          <span className="rs-lc-option-text">
            <span className="rs-lc-option-label">Email</span>
            <span className="rs-lc-option-hint">Write to us</span>
          </span>
        </button>
      </div>
      <p className="modal-or-line">or call us directly</p>
      <a
        href={COMPANY_PHONE_HREF}
        className="direct-call-link"
        aria-label={`Call ${COMPANY_NAME} at ${COMPANY_PHONE_DISPLAY}`}
      >
        {COMPANY_PHONE_DISPLAY}
      </a>
    </div>
  );

  const renderForm = () => {
    const showDateTimePicker = contactMethodRequiresDateTime(selectedOption);
    const isEmail = selectedOption === "Email";
    const isPhoneOrEmail = isEmail || selectedOption === "Phone Call";
    const formCopy =
      CONTACT_FORM_COPY[selectedOption] || {
        eyebrow: selectedOption || "",
        headline: "Tell Us About Your Project",
        subtext: "",
      };

    const addressInputClass = [
      errors.address && "field-error",
      !errors.address && addressConfirmed && formData.address.trim() && "validated",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="rs-lc-step rs-lc-step--form">
        <button
          type="button"
          className="rs-lc-back"
          onClick={() => {
            setSelectedOption(null);
            setErrors({});
          }}
        >
          <LcIconArrowLeft size={18} />
          Back
        </button>
        <p className="rs-lc-kicker">{formCopy.eyebrow}</p>
        <h2 className="rs-lc-title">{formCopy.headline}</h2>
        {formCopy.subtext ? (
          <p className="rs-lc-path-hint">{formCopy.subtext}</p>
        ) : null}
        <Form className="rs-lc-form">
          <div className="rs-lc-form-fields">
            {isPhoneOrEmail ? (
              <div className="rs-lc-form-row rs-lc-form-row--2">
                <Form.Group className="rs-lc-field">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={errors.fullName ? "field-error" : undefined}
                    aria-invalid={errors.fullName ? true : undefined}
                  />
                  {errors.fullName && (
                    <span className="field-error-message" role="alert">
                      {errors.fullName}
                    </span>
                  )}
                </Form.Group>
                <Form.Group className="rs-lc-field">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? "field-error" : undefined}
                    aria-invalid={errors.phone ? true : undefined}
                  />
                  {errors.phone && (
                    <span className="field-error-message" role="alert">
                      {errors.phone}
                    </span>
                  )}
                </Form.Group>
              </div>
            ) : (
              <Form.Group className="rs-lc-field">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={errors.fullName ? "field-error" : undefined}
                  aria-invalid={errors.fullName ? true : undefined}
                />
                {errors.fullName && (
                  <span className="field-error-message" role="alert">
                    {errors.fullName}
                  </span>
                )}
              </Form.Group>
            )}

            <Form.Group className="rs-lc-field">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? "field-error" : undefined}
                aria-invalid={errors.email ? true : undefined}
              />
              {errors.email && (
                <span className="field-error-message" role="alert">
                  {errors.email}
                </span>
              )}
            </Form.Group>

            <Form.Group className="rs-lc-field">
              <Form.Label>Address</Form.Label>
              <div className="rs-lc-autocomplete-wrap">
                <Autocomplete
                  options={GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS}
                  onLoad={(auto) => setAutocomplete(auto)}
                  onPlaceChanged={handlePlaceSelect}
                >
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleAddressInputChange}
                    placeholder="Start typing and select from suggestions"
                    autoComplete="street-address"
                    ref={autocompleteRef}
                    className={addressInputClass}
                    aria-invalid={errors.address ? true : undefined}
                    aria-describedby={errors.address ? "rs-lc-address-error" : undefined}
                  />
                </Autocomplete>
              </div>
              {errors.address && (
                <span
                  className="field-error-message"
                  id="rs-lc-address-error"
                  role="alert"
                >
                  {errors.address}
                </span>
              )}
            </Form.Group>

            <Form.Group className="rs-lc-field">
              <Form.Label>Project Type</Form.Label>
              <Form.Select
                name="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                className={errors.projectType ? "field-error" : undefined}
                aria-invalid={errors.projectType ? true : undefined}
              >
                <option value="">Select</option>
                {PROJECT_TYPES.map((type, index) => (
                  <option key={index}>{type}</option>
                ))}
              </Form.Select>
              {errors.projectType && (
                <span className="field-error-message" role="alert">
                  {errors.projectType}
                </span>
              )}
            </Form.Group>

            {showDateTimePicker && (
              <div className="rs-lc-form-row rs-lc-form-row--2 rs-lc-date-time-row">
                <Form.Group className="rs-lc-field">
                  <Form.Label>
                    {selectedOption === "Phone Call"
                      ? "Preferred Call Date"
                      : "Preferred Visit Date"}
                  </Form.Label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(d) => {
                      setSelectedDate(d);
                      clearErrorKey(setErrors, "selectedDate");
                    }}
                    dateFormat="yyyy-MM-dd"
                    className={[
                      "form-control",
                      "rs-lc-control",
                      errors.selectedDate ? "field-error" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    wrapperClassName="rs-lc-datepicker-wrap"
                    minDate={new Date()}
                  />
                  {errors.selectedDate && (
                    <span className="field-error-message" role="alert">
                      {errors.selectedDate}
                    </span>
                  )}
                </Form.Group>
                <Form.Group className="rs-lc-field">
                  <Form.Label>
                    {selectedOption === "Phone Call"
                      ? "Preferred Call Time"
                      : "Preferred Visit Time"}
                  </Form.Label>
                  <DatePicker
                    selected={selectedTime}
                    onChange={(t) => {
                      setSelectedTime(t);
                      clearErrorKey(setErrors, "selectedTime");
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className={[
                      "form-control",
                      "rs-lc-control",
                      errors.selectedTime ? "field-error" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    wrapperClassName="rs-lc-datepicker-wrap"
                  />
                  {errors.selectedTime && (
                    <span className="field-error-message" role="alert">
                      {errors.selectedTime}
                    </span>
                  )}
                </Form.Group>
              </div>
            )}

            <Form.Group className="rs-lc-field">
              <Form.Label>Project Description</Form.Label>
              <Form.Control
                as="textarea"
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleInputChange}
                rows={4}
                className={errors.projectDescription ? "field-error" : undefined}
                aria-invalid={errors.projectDescription ? true : undefined}
              />
              {errors.projectDescription && (
                <span className="field-error-message" role="alert">
                  {errors.projectDescription}
                </span>
              )}
            </Form.Group>
          </div>

          {/* Submit Button */}
          <div className="rs-lc-actions">
            <button
              type="button"
              className="rs-lc-submit"
              onClick={handleConfirmAppointment}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="rs-lc-spinner"
                />
              ) : (
                <>
                  {BUTTON_TEXT[selectedOption]} <span aria-hidden>→</span>
                </>
              )}
            </button>
          </div>
        </Form>
      </div>
    );
  };

  const renderThankYou = () => {
    const first = thankYouSummary?.firstName || "there";
    const followUp = thankYouSummary?.isPhoneCall
      ? "at your scheduled time"
      : "within 24 hours";

    return (
      <div className="success-state rs-lc-success-state">
        <div className="success-state-icon" aria-hidden>
          <LcIconCheckCircle size={56} />
        </div>
        <h3 className="success-state-title">We got your message!</h3>
        <p className="success-state-body">
          Thanks, {first}. We&apos;ll be in touch {followUp}.
        </p>
        <p className="contact-fallback">
          Questions? Call us at{" "}
          <a href={COMPANY_PHONE_HREF}>{COMPANY_PHONE_DISPLAY}</a>
        </p>
        <button
          type="button"
          className="rs-lc-success-close"
          onClick={closeModalView}
        >
          Close
        </button>
      </div>
    );
  };

  return (
    <Modal
      show={letsconnect}
      onHide={closeModalView}
      onExited={handleModalExited}
      enforceFocus={false}
      restoreFocus={false}
      mountOnEnter
      unmountOnExit
      centered
      className="rs-lets-connect-modal"
      dialogClassName="rs-lets-connect-dialog"
      contentClassName="rs-lets-connect-content"
      backdropClassName="rs-lets-connect-backdrop"
    >
      <div className="rs-lc-accent" aria-hidden />
      <button type="button" className="rs-lc-close" onClick={closeModalView} aria-label="Close">
        <LcIconClose size={22} />
      </button>
      <Modal.Body className="rs-lc-body">
        <div
          className={[
            "rs-lc-inner",
            !selectedOption && !showThankYou ? "rs-lc-inner--wide" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {!selectedOption && !showThankYou && renderOptionSelection()}
          {selectedOption && !showThankYou && renderForm()}
          {showThankYou && renderThankYou()}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LetsConnect;